import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs" with { type: "file" };
import { getDocument, PDFWorker } from "pdfjs-dist";
import { MAX_ROAST_PDF_ITEMS, MAX_ROAST_PDF_PAGES, MAX_ROAST_PDF_TEXT, parseCropsterPdf, type PdfText } from "@anthemreactv2/roast-domain";

/** A dedicated, local worker can be terminated on cancellation or a parser deadline. */
export async function readRoastPdf(bytes: Uint8Array, signal?: AbortSignal) {
  if (new TextDecoder().decode(bytes.subarray(0, 5)) !== "%PDF-") throw new Error("This file is not a PDF. Choose the original Cropster PDF export.");
  if (signal?.aborted) throw new Error("Import cancelled.");
  const port = new Worker(pdfWorkerUrl, { type: "module" });
  const worker = PDFWorker.create({ port });
  const task = getDocument({ data: bytes, worker, isEvalSupported: false, useSystemFonts: true, disableFontFace: true, verbosity: 0 });
  let cancel: () => void = () => {};
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    const stopped = new Promise<never>((_, reject) => {
      cancel = () => reject(new Error("Import cancelled."));
      signal?.addEventListener("abort", cancel, { once: true });
      timeout = setTimeout(() => reject(new Error("The PDF took too long to read. Export a smaller report for one roast.")), 20_000);
    });
    return await Promise.race([stopped, (async () => {
      const pdf = await task.promise;
      if (pdf.numPages > MAX_ROAST_PDF_PAGES) throw new Error("Import a roast PDF with at most 60 pages.");
      const pages: PdfText[][] = [];
      let itemCount = 0; let textCount = 0;
      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
        const page = await pdf.getPage(pageNumber);
        const reader = page.streamTextContent().getReader();
        const items: PdfText[] = [];
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            for (const item of value.items) {
              if (!("str" in item)) continue;
              itemCount++; textCount += item.str.length;
              if (itemCount > MAX_ROAST_PDF_ITEMS || textCount > MAX_ROAST_PDF_TEXT) throw new Error("The PDF text is too large. Export only one roast’s details and readings.");
              items.push({ text: item.str, x: item.transform[4], y: item.transform[5], width: item.width });
            }
          }
        } finally { reader.releaseLock(); }
        pages.push(items);
        page.cleanup();
      }
      return parseCropsterPdf(pages);
    })()]);
  } catch (error) {
    if (error instanceof Error && error.name === "PasswordException") throw new Error("This PDF is password-protected. Export an unencrypted report before importing.");
    throw error;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", cancel);
    // Do not wait on an unresponsive worker after the deadline or cancellation.
    void task.destroy().catch(() => {});
    worker.destroy();
    port.terminate();
  }
}
